import { Suspense, useRef } from 'react';
import { Button } from '../../../components/Button';
import { useCatalogLinks } from '../../../components/CatalogLinks';
import { Eyebrow } from '../../../components/Eyebrow';
import { MeasuredCards } from '../../../components/MeasuredCards';
import { PlatformMark } from '../../../components/PlatformMark';
import { WalletCta } from '../../../components/WalletCta';
import { Band } from '../../../components/editorial/Band';
import type { CategoryId } from '../../../features/catalog/model';
import { hasBenchmarkPopulation } from '../../../lib/benchmarks';
import { canChartUsage } from '../../../lib/networkUsage';
import { EndpointHeader, type EndpointHeaderProps } from '../EndpointHeader';
import { estimatePrice, rateText } from '../costMath';
import { Connector } from './Connector';
import { useRevealHit } from './useRevealHit';
import { HERO_UI_CSS } from './heroUi.css';
import { ReturnedCard } from './ReturnedCard';
import { highlightedFields, itemSchemaFields, sampleItem, type SampleItem } from '../screens/kit/sampleItem';
import { sampleCsvHref, sampleFileName, sampleJsonHref } from '../sampleDownload';
import { screenFor } from '../screens/screenRegistry';
import { m } from '../../../paraglide/messages';
import { ParaglideMessage } from '@inlang/paraglide-js-react';

interface HeroProps extends EndpointHeaderProps {
  /** the SKU's response JSON Schema, when the page has it */
  outputSchema?: unknown;
}

type BodyProps = HeroProps & { sample: SampleItem; Screen: NonNullable<ReturnType<typeof screenFor>> };

/** "Product screenshot in a browser": the pitch on the left; on the right
 *  a Chrome window on the page the sample was read from, bleeding off the page,
 *  with the sample item ringed and a card of what the API returned for it. */
const EndpointHeroBody = ({ api, endpoint, icon, usage, benchmark, window, outputSchema, sample, Screen }: BodyProps) => {
  const links = useCatalogLinks();
  const stage = useRef<HTMLDivElement>(null);
  const hit = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLElement>(null);
  const moves = useRevealHit(stage, hit);
  const category = api.category as CategoryId;
  const csvHref = sampleCsvHref(endpoint);
  const jsonHref = sampleJsonHref(endpoint);
  const showUptime = benchmark ? hasBenchmarkPopulation(benchmark.uptimeSample) : false;
  const showLatency = benchmark ? hasBenchmarkPopulation(benchmark.latencySample) : false;
  return (
    <div className="overflow-x-clip">
      <style>{HERO_UI_CSS}</style>
      <Band>
        <div className="px-6 py-16 sm:px-8 md:py-20 lg:py-24 lg:pl-10 xl:pl-14">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] lg:items-center xl:grid-cols-[minmax(0,32rem)_minmax(0,1fr)] xl:gap-14">
            <div className="min-w-0">
              <Eyebrow to={links.api(api.id)}>{m.endpoint_header_eyebrow({ platform: api.name })}</Eyebrow>
              <div className="mt-3 flex items-center gap-4">
                <PlatformMark id={api.id} icon={icon} category={category} size={40} iconSize={22} />
                <h1 className="display-2 leading-tight text-text">
                  <ParaglideMessage
                    message={m.endpoint_header_title}
                    inputs={{ platform: api.name, action: endpoint.action }}
                    markup={{ muted: ({ children }) => <span className="muted-word">{children}</span> }}
                  />
                </h1>
              </div>
              <p className="lede mt-5">{endpoint.description}</p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <WalletCta label={m.endpoint_hero_start_free()} className="h-10 px-4.5 text-14" />
                {csvHref ? (
                  <Button as="a" href={csvHref} download={sampleFileName(endpoint, 'csv')} variant="line" className="h-10 px-4.5 text-14">
                    {m.endpoint_hero_download_sample()}
                  </Button>
                ) : null}
                {jsonHref ? (
                  <a
                    href={jsonHref}
                    download={sampleFileName(endpoint, 'json')}
                    className="text-13 text-text-2 underline decoration-line-2 underline-offset-4 hover:text-accent"
                  >
                    {m.endpoint_hero_or_json()}
                  </a>
                ) : null}
              </div>
              <p className="mt-5 text-13 text-text-3">
                <ParaglideMessage
                  message={m.endpoint_hero_rate_note}
                  inputs={{ rate: rateText(estimatePrice(endpoint)) }}
                  markup={{ rate: ({ children }) => <span className="mono text-text-2">{children}</span> }}
                />
              </p>
            </div>
            <div ref={stage} className="relative min-w-0 lg:pb-44">
              {/* A picture of the source page, in its own language on every locale. */}
              <div lang="en" className="hero-zoom lg:w-300">
                <Suspense fallback={null}>
                  <Screen endpoint={endpoint} hitRef={hit} />
                </Suspense>
              </div>
              <ReturnedCard
                cardRef={card}
                listKey={sample.listKey}
                item={sample.item}
                highlighted={highlightedFields(endpoint, sample)}
                schemaFields={itemSchemaFields(outputSchema, sample.listKey)}
                mark={<PlatformMark id={api.id} icon={icon} category={category} size={18} iconSize={11} radius={5} />}
                className="relative z-10 mx-3 mt-4 lg:absolute lg:bottom-0 lg:-left-12 lg:m-0 lg:w-84"
              />
              <Connector stage={stage} from={hit} to={card} moves={moves} />
            </div>
          </div>
          <MeasuredCards
            className="mt-14"
            uptimePct={showUptime && benchmark ? benchmark.uptimePct : undefined}
            uptimeSample={benchmark?.uptimeSample}
            latencyP50Ms={showLatency && benchmark ? benchmark.latencyP50Ms : undefined}
            window={window}
            requests={usage?.requests ?? 0}
            weekly={canChartUsage(usage) ? usage.weekly : undefined}
          />
        </div>
      </Band>
    </div>
  );
};

/** An endpoint with no drawing, or no sample item to list, keeps the stock header. */
export const EndpointHero = (props: HeroProps) => {
  const Screen = screenFor(props.endpoint.sku);
  const sample = Screen ? sampleItem(props.endpoint) : null;
  return Screen && sample ? <EndpointHeroBody {...props} sample={sample} Screen={Screen} /> : <EndpointHeader {...props} />;
};
